import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Sprout, Leaf, BookOpen, FlaskConical,
  Newspaper, Bug, LogOut, Settings, ChevronLeft, ChevronRight,
  CalendarCheck, Menu, X, Scale, CalendarDays, Shield
} from 'lucide-react';
import CannabisLeaf from './CannabisLeaf';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/projects', icon: Sprout, label: 'Projekte' },
  { to: '/plants', icon: Leaf, label: 'Pflanzen' },
  { to: '/care-log', icon: CalendarCheck, label: 'Pflege' },
  { to: '/calendar', icon: CalendarDays, label: 'Kalender' },
  { to: '/diary', icon: BookOpen, label: 'Tagebuch' },
  { to: '/strains', icon: FlaskConical, label: 'Strains' },
  { to: '/harvests', icon: Scale, label: 'Ernte' },
  { to: '/diseases', icon: Bug, label: 'Krankheiten' },
  { to: '/newsfeed', icon: Newspaper, label: 'Newsfeed' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
        : 'text-emerald-100/60 hover:bg-[#111C15] hover:text-emerald-100'
    }`;

  const mobileLinkClass = ({ isActive }) =>
    `flex flex-col items-center gap-1 px-2 py-1 rounded-lg transition-colors ${
      isActive ? 'text-emerald-400' : 'text-emerald-100/50 hover:text-emerald-100'
    }`;

  const mobileNavItems = navItems.slice(0, 5);

  return (
    <div className="min-h-screen flex" style={{ background: '#050A07' }}>
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex flex-col border-r transition-all duration-300 ${collapsed ? 'w-16' : 'w-56'}`}
        style={{ background: '#0A120D', borderColor: '#1B2A21', position: 'sticky', top: 0, height: '100vh' }}>
        {/* Logo */}
        <div className="p-4 flex items-center gap-3 border-b" style={{ borderColor: '#1B2A21' }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
            <CannabisLeaf size={16} color="white" />
          </div>
          {!collapsed && <span className="font-heading font-bold text-emerald-50 text-lg">GrowManager</span>}
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, exact }) => (
            <NavLink key={to} to={to} end={exact} className={linkClass} data-testid={`nav-${label.toLowerCase()}`}>
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t space-y-1" style={{ borderColor: '#1B2A21' }}>
          <NavLink to="/settings" className={linkClass} data-testid="nav-settings">
            <Settings size={18} className="flex-shrink-0" />
            {!collapsed && <span>Einstellungen</span>}
          </NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              <Shield size={18} className="flex-shrink-0" />
              {!collapsed && <span>Admin</span>}
            </NavLink>
          )}
          {!collapsed && (
            <div className="px-3 py-2 text-xs text-emerald-100/40 truncate">{user?.name || user?.email}</div>
          )}
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
            data-testid="logout-btn">
            <LogOut size={18} className="flex-shrink-0" />
            {!collapsed && <span>Abmelden</span>}
          </button>
          <button onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-emerald-100/40 hover:text-emerald-100 hover:bg-[#111C15] transition-colors">
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="md:hidden flex items-center justify-between p-4 border-b sticky top-0 z-40"
          style={{ background: 'rgba(5,10,7,0.9)', backdropFilter: 'blur(10px)', borderColor: '#1B2A21' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
              <CannabisLeaf size={14} color="white" />
            </div>
            <span className="font-heading font-bold text-emerald-50">GrowManager</span>
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 rounded-lg text-emerald-100/60 hover:text-emerald-100 hover:bg-[#111C15] transition-colors">
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </header>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
            <div className="relative w-64 h-full flex flex-col p-4 space-y-1 z-10"
              style={{ background: '#0A120D', borderRight: '1px solid #1B2A21' }}>
              <div className="flex items-center gap-2 mb-4 pb-4 border-b" style={{ borderColor: '#1B2A21' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}>
                  <CannabisLeaf size={16} color="white" />
                </div>
                <span className="font-heading font-bold text-emerald-50 text-lg">GrowManager</span>
              </div>
              {navItems.map(({ to, icon: Icon, label, exact }) => (
                <NavLink key={to} to={to} end={exact}
                  className={linkClass}
                  onClick={() => setMobileOpen(false)}>
                  <Icon size={18} />
                  <span>{label}</span>
                </NavLink>
              ))}
              <div className="!mt-auto pt-4 border-t space-y-1" style={{ borderColor: '#1B2A21' }}>
                <NavLink to="/settings" className={linkClass} onClick={() => setMobileOpen(false)}>
                  <Settings size={18} /><span>Einstellungen</span>
                </NavLink>
                {user?.role === 'admin' && (
                  <NavLink to="/admin" className={linkClass} onClick={() => setMobileOpen(false)} data-testid="mobile-nav-admin">
                    <Shield size={18} /><span>Admin</span>
                  </NavLink>
                )}
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors">
                  <LogOut size={18} /><span>Abmelden</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto pb-safe md:pb-6">
          <Outlet />
        </main>

        {/* Mobile Bottom Nav */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around px-2 py-2 border-t bottom-nav"
          style={{ background: 'rgba(10,18,13,0.95)', backdropFilter: 'blur(10px)', borderColor: '#1B2A21' }}>
          {mobileNavItems.map(({ to, icon: Icon, label, exact }) => (
            <NavLink key={to} to={to} end={exact} className={mobileLinkClass} data-testid={`mobile-nav-${label.toLowerCase()}`}>
              <Icon size={20} />
              <span className="text-xs">{label}</span>
            </NavLink>
          ))}
          <button onClick={() => setMobileOpen(true)} className="flex flex-col items-center gap-1 px-2 py-1 rounded-lg text-emerald-100/50 hover:text-emerald-100 transition-colors">
            <Menu size={20} />
            <span className="text-xs">Mehr</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
